import { inject, Inject, Injectable } from '@angular/core';
import {
  AutomationReplacementChipsDataService,
  PagedResponse,
} from '../common/components/automation-replacement-chips/interfaces';
import { combineLatest, map, Observable, of, ReplaySubject, take } from 'rxjs';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { switchMap } from 'rxjs/operators';
import { FilterGroupOperator, FilterOperator, ListDefinition, ListDefinitionsApiService } from '@vendasta/bulk-actions';
import { ObjectType } from '@galaxy/crm/static';

interface ExtraData {
  crmObjectSubtype: string;
}

@Injectable({ providedIn: 'root' })
export class CRMListsSelectorService implements AutomationReplacementChipsDataService<ListDefinition> {
  private readonly listsService = inject(ListDefinitionsApiService);
  private readonly objectType$$ = new ReplaySubject<ObjectType>(1);

  supportsSearching = true;

  constructor(@Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>) {}

  setObjectType(objectType: ObjectType): void {
    this.objectType$$.next(objectType);
  }

  doLookup(
    searchTerm: string,
    cursor: string,
    pageSize: number,
    extraData?: ExtraData,
  ): Observable<PagedResponse<ListDefinition>> {
    return combineLatest([this.namespace$, this.objectType$$]).pipe(
      switchMap(([namespace, objectType]) => {
        const resourceType = this.getResourceType(objectType, extraData?.crmObjectSubtype);
        return this.listsService.listDefinitions({
          namespace: namespace,
          filters: {
            operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
            filters: [
              {
                fieldId: 'resource_type',
                operator: FilterOperator.FILTER_OPERATOR_IS,
                values: [{ string: resourceType }],
              },
            ],
          },
          pagingOptions: {
            cursor: cursor,
            pageSize: pageSize,
          },
        });
      }),
      take(1),
      map((res) => {
        let results = res?.listDefinitions;
        if (searchTerm !== '') {
          results = results.filter((list) => list?.name.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        const nextCursor = res?.pagingMetadata?.nextCursor || '';
        const hasMore = res?.pagingMetadata?.hasMore || false;
        return { results, nextCursor, hasMore };
      }),
    );
  }

  private getResourceType(objectType: string, objectSubtype?: string): string {
    if (objectType === 'CustomObject' && objectSubtype) {
      return `${objectType}::${objectSubtype}`;
    }
    return objectType;
  }

  convertDataToDisplay(s: ListDefinition): Observable<string> {
    return of(s?.name ?? '');
  }

  convertDataToFormValue(s: ListDefinition): any {
    return s.listId;
  }

  getMulti(x: string[]): Observable<ListDefinition[]> {
    if (x?.length === 0 || x[0] === '') {
      return of([ListDefinition.fromProto({})]);
    }

    return this.namespace$.pipe(
      switchMap((namespace) => {
        return this.listsService.getMultiListDefinition({
          namespace: namespace,
          listIds: x,
        });
      }),
      map((response) => response.listDefinitions),
      take(1),
    );
  }

  get(x: string): Observable<ListDefinition> {
    return this.getMulti([x]).pipe(map((list) => list[0]));
  }
}
